"use client";
import Link from "next/link";
import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import Image from "next/image";

type Particle = {
  id: number;
  x: number;
  y: number;
  size: number;
  delay: number;
  duration: number;
  color: string;
};

function getLocale(): string {
  if (typeof document === "undefined") return "en-US";
  const match = document.cookie.match(/NEXT_LOCALE=([^;]+)/);
  return match ? match[1] : "en-US";
}

function getLabels(locale: string) {
  const labels: Record<string, { title: string; sub: string; home: string; back: string }> = {
    "en-US": { title: "Page not found", sub: "Our AI is investigating the disappearance.", home: "Go home", back: "Go back" },
    "ru-RU": { title: "Страница не найдена", sub: "ИИ расследует исчезновение.", home: "На главную", back: "Назад" },
    "es-MX": { title: "Página no encontrada", sub: "Nuestra IA está investigando la desaparición.", home: "Inicio", back: "Volver" },
    "pt-BR": { title: "Página não encontrada", sub: "Nossa IA está investigando o desaparecimento.", home: "Início", back: "Voltar" },
    "fr-FR": { title: "Page introuvable", sub: "Notre IA enquête sur la disparition.", home: "Accueil", back: "Retour" },
    "ar-AR": { title: "الصفحة غير موجودة", sub: "ذكاؤنا الاصطناعي يحقق في الاختفاء.", home: "الرئيسية", back: "رجوع" },
    "hi-IN": { title: "पृष्ठ नहीं मिला", sub: "हमारा AI गायब होने की जांच कर रहा है।", home: "मुखपृष्ठ", back: "वापस" },
    "ja-JP": { title: "ページが見つかりません", sub: "AIが消失を調査中です。", home: "ホームへ", back: "戻る" },
    "ko-KR": { title: "페이지를 찾을 수 없습니다", sub: "AI가 사라진 원인을 조사 중입니다.", home: "홈으로", back: "돌아가기" },
  };
  return labels[locale] ?? labels["en-US"];
}
type DiagLine = {
  text: string;
  delay: number;
  indent?: boolean;
  error?: boolean;
  success?: boolean;
  blink?: boolean;
};

const DIAGNOSTIC_LINES: DiagLine[] = [
  { text: "> initializing diagnostic protocol...", delay: 300 },
  { text: "> scanning route table... ", delay: 800 },
  { text: "  └─ matched: 0 / 1024", delay: 1400, indent: true },
  { text: "> querying database...", delay: 2000 },
  { text: "  └─ page_id: NULL", delay: 2600, indent: true, error: true },
  { text: "  └─ status: 404 NOT_FOUND", delay: 3000, indent: true, error: true },
  { text: "> analyzing user intent...", delay: 3600 },
  { text: "  └─ suggesting: /, /pricing, /faq", delay: 4200, indent: true, success: true },
  { text: "> running neural self-check...", delay: 5400 },
  { text: "  └─ ERROR: diagnostic failed", delay: 6200, indent: true, error: true },
  { text: "  └─ restarting in 3...", delay: 7000, indent: true },
];

export default function NotFound() {
  const t = useTranslations("notFound");
  const [particles, setParticles] = useState<Particle[]>([]);
  const [visibleLines, setVisibleLines] = useState<number>(0);
  const [typedChars, setTypedChars] = useState<Record<number, number>>({});
 

  useEffect(() => {
    

    const colors = ["#3b82f6", "#60a5fa", "#93c5fd", "#dbeafe", "#fbbf24"];
    const generated: Particle[] = Array.from({ length: 60 }, (_, i) => ({
      id: i,
      x: Math.random() * 60,
      y: Math.random() * 80 + 10,
      size: Math.random() * 8 + 3,
      delay: Math.random() * 4,
      duration: Math.random() * 3 + 3,
      color: colors[Math.floor(Math.random() * colors.length)],
    }));
    setParticles(generated);
  }, []);

  // Появление строк
  useEffect(() => {
    let timers: NodeJS.Timeout[] = [];
    let intervals: NodeJS.Timeout[] = [];

    const runCycle = () => {
      // Очистка перед новым циклом
      setVisibleLines(0);
      setTypedChars({});

      DIAGNOSTIC_LINES.forEach((line, idx) => {
        timers.push(setTimeout(() => {
          setVisibleLines((prev) => Math.max(prev, idx + 1));
          let i = 0;
          const typeInterval = setInterval(() => {
            i++;
            setTypedChars((prev) => ({ ...prev, [idx]: i }));
            if (i >= line.text.length) clearInterval(typeInterval);
          }, 25);
          intervals.push(typeInterval);
        }, line.delay));
      });
    };

    runCycle();
    // Перезапуск каждые 9 сек (после последней строки + пауза)
    const cycleInterval = setInterval(runCycle, 12000);

    return () => {
      timers.forEach(clearTimeout);
      intervals.forEach(clearInterval);
      clearInterval(cycleInterval);
    };
  }, []);

 return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6 py-12 overflow-hidden relative">
      {/* Декор: летающие квадратики */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden>
        {particles.map((p) => (
          <span
            key={p.id}
            className="absolute particle-drift"
            style={{
              left: `${p.x}%`,
              top: `${p.y}%`,
              width: `${p.size}px`,
              height: `${p.size}px`,
              backgroundColor: p.color,
              animationDelay: `${p.delay}s`,
              animationDuration: `${p.duration}s`,
              opacity: 0,
            }}
          />
        ))}
      </div>

      {/* Контент */}
      <div className="relative z-10 max-w-6xl w-full grid md:grid-cols-3 gap-6 items-center">
        {/* Колонка 1 — 404, заголовок, кнопки */}
        <div className="text-center md:text-start">
          <p className="text-7xl font-bold text-slate-900 tracking-tight mb-3">404</p>
          <h1 className="text-2xl font-semibold text-slate-900 mb-2">{t("title")}</h1>
          <p className="text-sm text-slate-500 mb-6">{t("sub")}</p>
          <div className="flex flex-wrap gap-3 justify-center md:justify-start">
            <Link
              href="/"
              className="px-5 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-medium hover:bg-slate-700 transition-colors"
            >
              {t("home")}
            </Link>
            <button
              onClick={() => window.history.back()}
              className="px-5 py-2.5 rounded-xl border border-slate-200 text-slate-700 text-sm font-medium hover:border-slate-300 hover:bg-slate-50 transition-all"
            >
              {t("back")}
            </button>
          </div>
        </div>

        {/* Колонка 2 — терминал */}
        <div className="bg-slate-900 rounded-2xl shadow-2xl overflow-hidden border border-slate-800 z-10 scale-95">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-800 bg-slate-950">
            <span className="w-3 h-3 rounded-full bg-red-500" />
            <span className="w-3 h-3 rounded-full bg-yellow-500" />
            <span className="w-3 h-3 rounded-full bg-green-500" />
            <span className="ms-3 text-xs text-slate-400 font-mono">ai-diagnostic.log</span>
          </div>
          <div className="p-5 font-mono text-[13px] leading-relaxed min-h-[280px]" dir="ltr">
            {DIAGNOSTIC_LINES.slice(0, visibleLines).map((line, idx) => {
              const charCount = typedChars[idx] ?? 0;
              const shown = line.text.slice(0, charCount);
              const isLast = idx === visibleLines - 1;
              const typing = charCount < line.text.length;

              return (
                <div
                  key={idx}
                  className={`whitespace-pre ${
                    line.error
                      ? "text-red-400"
                      : line.success
                      ? "text-green-400"
                      : line.indent
                      ? "text-slate-400"
                      : "text-blue-300"
                  }`}
                >
                  {shown}
                  {isLast && (typing || line.blink) && (
                    <span className="inline-block w-2 h-3.5 bg-blue-400 ms-0.5 align-middle animate-pulse" />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Колонка 3 — голова */}
        <div className="relative md:-ms-40 lg:-ms-110">
          <Image
            src="/illustrations/404-head.png"
            alt="404"
            width={512}
            height={512}
            className="w-full max-w-lg h-auto object-contain scale-150"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
            draggable={false}
          />
        </div>
      </div>
    </div>
  );
}